import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { animated, useSpring } from '@react-spring/web';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
}) => {
  const modal = useRef<HTMLDivElement>(null);
  const [animationData, setAnimationData] = useState<any>(null);

  // 🔁 Cargar animación solo cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      import('../../assets/animations/alerts/alerta-error.json').then((data) =>
        setAnimationData(data.default)
      );
    } else {
      setAnimationData(null);
    }
  }, [isOpen]);

  // ⛔ Cierre por click fuera del modal
  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      if (modal.current && !modal.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [onClose]);

  // ⎋ Cierre por tecla Escape o Enter
  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Escape' || key === 'Enter') {
        onClose();
      }
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [onClose]);

  // 🌀 Animación de entrada
  const animationStyle = useSpring({
    opacity: isOpen ? 1 : 0,
    config: { duration: 300 },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 top-0 z-[999999] flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
      <animated.div
        style={animationStyle}
        ref={modal}
        className="w-full max-w-md rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark shadow-lg"
      >
        {/* 🛑 Solo mostrar si cargó la animación */}
        {animationData && (
          <div className="mb-2 flex justify-center">
            <Lottie animationData={animationData} loop={false} style={{ width: 60, height: 60 }} />
          </div>
        )}

        <h3 className="pb-2 text-xl font-bold text-[#DC3545] dark:text-[#DC3545] sm:text-2xl">
          {title}
        </h3>

        <p className="text-gray-800 dark:text-gray-300 mb-6">{message}</p>

        <button
          onClick={onClose}
          className="block w-full rounded border border-[#DC3545] bg-[#DC3545] p-3 text-center font-medium text-white transition hover:bg-opacity-90"
        >
          Cerrar
        </button>
      </animated.div>
    </div>
  );
};

export default React.memo(ErrorModal);
